import { useIntl } from "react-intl";
import type { UsageCardLoaderProps } from "./UsageCard.js";
import { UsageCard } from "./UsageCard.js";
import { calculateUsageCost, TieredPriceProductId } from "@project/common";
import { IconMessage2Down, IconMessage2Up } from "@tabler/icons-react";
import { INTERNATIONALIZED_UNIT_LABELS } from "../../../constants/productLabels.js";
import { useAITokensUsageSums } from "./hooks/fetchSums.js";
import { useProductDescriptions, useProductNames } from "./hooks/productLabels.js";

interface AITokensUsageCardProps extends UsageCardLoaderProps {
  inputTokenProductId?: TieredPriceProductId;
  outputTokenProductId?: TieredPriceProductId;
}

export function AITokensUsageCard({
  from,
  to,
  interval,
  inputTokenProductId,
  outputTokenProductId,
  isLoadingSubscriptions,
}: AITokensUsageCardProps) {
  const intl = useIntl();
  const {
    inputTokensUsageSum,
    outputTokensUsageSum,
    isLoadingAssistantUsage,
    isErrorAssistantUsage,
  } = useAITokensUsageSums({ from, to, interval });

  const getName = useProductNames();
  const getDescription = useProductDescriptions();

  const unitLabel = intl.formatMessage(INTERNATIONALIZED_UNIT_LABELS.token);

  return (
    <>
      <UsageCard
        unitLabel={unitLabel}
        iconColor="error.3"
        icon={<IconMessage2Up />}
        usageSum={inputTokensUsageSum}
        isError={isErrorAssistantUsage}
        name={getName(TieredPriceProductId.TieredInputToken)}
        defaultProductId={TieredPriceProductId.TieredInputToken}
        isLoading={isLoadingSubscriptions || isLoadingAssistantUsage}
        description={getDescription(TieredPriceProductId.TieredInputToken)}
        usageCost={calculateUsageCost(inputTokensUsageSum, inputTokenProductId)}
      />
      <UsageCard
        unitLabel={unitLabel}
        iconColor="warning.3"
        icon={<IconMessage2Down />}
        usageSum={outputTokensUsageSum}
        isError={isErrorAssistantUsage}
        name={getName(TieredPriceProductId.TieredOutputToken)}
        defaultProductId={TieredPriceProductId.TieredOutputToken}
        isLoading={isLoadingSubscriptions || isLoadingAssistantUsage}
        description={getDescription(TieredPriceProductId.TieredOutputToken)}
        usageCost={calculateUsageCost(outputTokensUsageSum, outputTokenProductId)}
      />
    </>
  );
};
